import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { sanitizeSearchInput } from '../utils/security';
import { useAuth } from '../contexts/AuthContext';
import {
  fetchBekleyenIslemler,
  fetchBekleyenIslemTercih,
  saveBekleyenIslemTercih,
  dispatchBekleyenIslemYenile,
  buildHedefSearch,
} from '../services/adminBekleyenIslemService';
import TableListFooter from '../components/TableListFooter';
import { PAGE_DEFAULT_LIMIT } from '../constants/pagination';

const SEARCH_DEBOUNCE_MS = 400;

const formatDateTime = (value) => {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  const msg = error?.message || error?.data?.mesaj || error?.data?.message || fallback;
  return msg === 'Failed to fetch' ? 'Sunucuya bağlanılamadı' : msg;
};

const BekleyenIslemlerPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.rol === 'admin';
  const [islemler, setIslemler] = useState([]);
  const [moduller, setModuller] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [modulFilter, setModulFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationMeta, setPaginationMeta] = useState({ total: 0, totalPages: 1 });
  const [hakedisBekleyenGoster, setHakedisBekleyenGoster] = useState(true);
  const [tercihLoading, setTercihLoading] = useState(isAdmin);
  const [tercihSaving, setTercihSaving] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, modulFilter]);

  useEffect(() => {
    if (!isAdmin) {
      setHakedisBekleyenGoster(true);
      setTercihLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setTercihLoading(true);
      try {
        const res = await fetchBekleyenIslemTercih();
        if (!cancelled) {
          setHakedisBekleyenGoster(res?.veri?.hakedisBekleyenGoster !== false);
        }
      } catch {
        if (!cancelled) setHakedisBekleyenGoster(true);
      } finally {
        if (!cancelled) setTercihLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  const loadList = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchBekleyenIslemler({
        sayfa: currentPage,
        limit: PAGE_DEFAULT_LIMIT,
        arama: debouncedSearch || undefined,
        modul: modulFilter || undefined,
      });
      if (res?.basarili && res?.veri) {
        setIslemler(res.veri.islemler || []);
        const p = res.veri.sayfalama || {};
        setPaginationMeta({
          total: p.toplam ?? 0,
          totalPages: Math.max(1, p.toplamSayfa ?? 1),
        });
        setModuller(Array.isArray(res.veri.moduller) ? res.veri.moduller : []);
        if (res?.veri?.tercih?.hakedisBekleyenGoster !== undefined) {
          setHakedisBekleyenGoster(res.veri.tercih.hakedisBekleyenGoster !== false);
        }
      } else {
        setIslemler([]);
        setPaginationMeta({ total: 0, totalPages: 1 });
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Liste yüklenemedi'));
      setIslemler([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, debouncedSearch, modulFilter]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    const handler = () => loadList();
    window.addEventListener('bekleyen-islem-yenile', handler);
    return () => window.removeEventListener('bekleyen-islem-yenile', handler);
  }, [loadList]);

  const modulOptions = useMemo(() => {
    const opts = [{ value: '', label: 'Tüm modüller' }];
    moduller.forEach((m) => opts.push({ value: m, label: m }));
    return opts;
  }, [moduller]);

  const handleHakedisToggle = async (checked) => {
    if (!isAdmin || tercihSaving) return;
    setHakedisBekleyenGoster(checked);
    setTercihSaving(true);
    try {
      await saveBekleyenIslemTercih({ hakedisBekleyenGoster: checked });
      dispatchBekleyenIslemYenile();
      await loadList();
    } catch (err) {
      setHakedisBekleyenGoster(!checked);
      setError(getErrorMessage(err, 'Tercih kaydedilemedi'));
    } finally {
      setTercihSaving(false);
    }
  };

  const handleIlerle = (item) => {
    const hedef = item?.hedef;
    if (!hedef?.path) return;
    const search = buildHedefSearch(hedef.query || {});
    navigate(`${hedef.path}${search}`);
  };

  return (
    <main className="main-content">
      <div className="content-header">
        <h1>Bekleyen İşlemler</h1>
      </div>

      <div className="admin-finance-toolbar">
        <div className="admin-finance-search-row">
          <div className="search-box admin-finance-search-box">
            <i className="fas fa-search" aria-hidden="true"></i>
            <input
              type="text"
              placeholder="Referans no veya teknik servis ara…"
              value={searchInput}
              onChange={(e) => setSearchInput(sanitizeSearchInput(e.target.value, { trimEdges: false }).substring(0, 100))}
              maxLength={100}
              aria-label="Bekleyen işlem ara"
            />
            {loading && debouncedSearch && (
              <i className="fas fa-spinner fa-spin admin-finance-search-spinner" aria-hidden="true"></i>
            )}
          </div>
          <select
            className="admin-finance-select"
            value={modulFilter}
            onChange={(e) => setModulFilter(e.target.value)}
            aria-label="Modül filtresi"
          >
            {modulOptions.map((o) => (
              <option key={o.value || 'all'} value={o.value}>{o.label}</option>
            ))}
          </select>
          <button
            type="button"
            className="secondary-btn"
            onClick={loadList}
            disabled={loading}
            style={{ flexShrink: 0 }}
          >
            <i className={`fas fa-sync${loading ? ' fa-spin' : ''}`} aria-hidden="true"></i>
            <span>Yenile</span>
          </button>
          {isAdmin ? (
            <label
              className="bekleyen-islem-tercih-label"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                flexShrink: 0,
                cursor: tercihSaving || tercihLoading ? 'wait' : 'pointer',
                userSelect: 'none',
                fontSize: '14px',
                color: 'var(--text-gray)',
              }}
            >
              <input
                type="checkbox"
                checked={hakedisBekleyenGoster}
                disabled={tercihSaving || tercihLoading}
                onChange={(e) => handleHakedisToggle(e.target.checked)}
                aria-label="Hakediş bekleyenleri göster"
              />
              <span>Hakediş bekleyenleri göster</span>
            </label>
          ) : null}
        </div>
      </div>

      <div className="card">
        {loading && islemler.length === 0 ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <div className="loading-text">Bekleyen işlemler yükleniyor</div>
          </div>
        ) : error ? (
          <div className="error-state">{error}</div>
        ) : islemler.length === 0 ? (
          <div className="empty-state" style={{ padding: '40px', textAlign: 'center' }}>
            <i className="fas fa-clipboard-check" style={{ fontSize: '32px', color: 'var(--text-light)', marginBottom: '12px' }}></i>
            <div style={{ color: 'var(--text-gray)', fontSize: '14px' }}>Bekleyen işlem bulunmuyor</div>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="admins-table">
                <thead>
                  <tr>
                    <th>Modül</th>
                    <th>Durum</th>
                    <th>Referans</th>
                    <th>Detay</th>
                    <th>Tarih</th>
                    <th className="islemler-sutunu">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {islemler.map((item) => (
                    <tr key={item.id}>
                      <td style={{ fontWeight: 500 }}>{item.modul || '—'}</td>
                      <td>{item.durumEtiketi || '—'}</td>
                      <td style={{ fontWeight: 500 }}>{item.baslik || '—'}</td>
                      <td>{item.altBaslik || '—'}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(item.tarih)}</td>
                      <td className="table-actions islemler-sutunu" style={{ whiteSpace: 'nowrap' }}>
                        <button
                          type="button"
                          className="category-action-btn edit-btn"
                          title="İlgili sayfaya git"
                          aria-label={`${item.baslik} — ilgili sayfaya git`}
                          onClick={() => handleIlerle(item)}
                        >
                          <i className="fas fa-arrow-right" aria-hidden="true"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <TableListFooter
              totalRecords={paginationMeta.total}
              currentPage={currentPage}
              totalPages={paginationMeta.totalPages}
              onPageChange={setCurrentPage}
              loading={loading}
              embedded
            />
          </>
        )}
      </div>
    </main>
  );
};

export default BekleyenIslemlerPage;
